import { ColumnSource, RowSource, RequestFilters } from "@omegagrid/grid";
import { ColorTypes, dates } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import rp, { GridEditorAdapter, GridEditorOptions, RowData, formatDateTime } from "@reporting/rp-app-host";
import { TableDefinition, SqlColumnDefinition } from "@reporting/rp-types/mdm";
import { SQL_READ_ONLY_COLS } from "../types";
import { AccountingRights } from "@reporting/rp-types/core";

const PAGE_SIZE = 500;

export class SqlValuesGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[] = [];
	private table: TableDefinition;
	private hasPrimaryKey: boolean = false;

	constructor (
		private idTable: number
	) { super() }

	async getColumns(): Promise<ColumnSource[]> {
		this.table = await rp.ms('mdm').get<TableDefinition>(`tableDefinitions/${this.idTable}`);
		if (!this.table) throw new Error('Table definition not found');
		const columnDefs = await rp.ms('mdm').get<SqlColumnDefinition[]>(`columnDefinitions/${this.table.id_md}`);
		this.hasPrimaryKey = columnDefs.some(col => col.column_name === 'IDZ');
		if (!this.hasPrimaryKey) {
			rp.host.bucket?.showAlert({content: `
				${msg('The table does not have a primary key column.')}(IDZ) 
				${msg('Editor functionality may be limited.')}
			`, type: 'warning'});
		}
		this._columns = [{
			data: 'controls',
			t: 'a',
			e: false,
			w: 5 + 20 * 2,
			content: [{
				action: 'delete',
				title: msg('Delete'),
				icon: 'trash',
				color: ColorTypes.Red
			}, {
				action: 'duplicate',
				title: msg('Duplicate'),
				icon: 'copy',
				color: ColorTypes.Yellow,
			}],
			filter: false,
			sorting: false,
			hidden: !this.hasPrimaryKey || !this.hasWriteAccess(),
		}];
		columnDefs.forEach((col) => {
			this._columns.push(this.getColumnSettingsFromDataType(col));
		});
		return this._columns;
	}

	getColumnSettingsFromDataType(col: SqlColumnDefinition): ColumnSource {
		const readOnly = SQL_READ_ONLY_COLS.includes(col.column_name) || !this.hasWriteAccess();
		let colSc: ColumnSource = {
			data: col.column_name,
			header: col.column_name,
			w: 'auto',
			t: 't',
			e: !readOnly
		}
		const matches = col.data_type.match(/([a-zA-Z]*)(?:\((\d*|max)\))?/);
		if (matches && matches[1]) {
			const type = matches[1].toLowerCase();
			if (['varchar', 'nvarchar', 'text', 'ntext', 'char', 'nchar'].includes(type)) {
				colSc.t = 't';
			} else if (['tinyint', 'smallint', 'int', 'bigint'].includes(type)) {
				colSc.t = 'n';
				colSc.z = '0';
			} else if (['float', 'real'].includes(type)) {
				colSc.t = 'n';
				colSc.z = '0.000';
			} else if (type === 'bit') {
				colSc.t = 'b';
			} else if (type === 'date') {
				colSc.t = 'd';
				colSc.z = dates.getDateFormat();
			} else if (['binary', 'varbinary'].includes(type)) {
				colSc.t = 't';
				colSc.e = false;
			}

			if (matches[2]) {
				//colSc.pattern = `^.{0,{${matches[2]}}}$`; //TODO: set pattern
			}
		}
		return colSc;
	}

	async getRows(filters?: RequestFilters): Promise<RowSource[]> {
		const values = await rp.ms('mdm').get<any[]>(`values/${this.table.id_md}`, this.createRequestParams(filters, {
			count: PAGE_SIZE
		}));
		return values.map(data => ({
			data: data,
			...(!this.hasPrimaryKey ? { e: false } : {})
		}));
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			columnSorting: true,
			// Keep newly inserted rows where the user created them; save() merges the server-generated
			// IDZ back in place. A full reload (refreshAfterSave) would re-sort and make the row jump.
			refreshAfterSave: false,
			filters: true,
			pageSize: PAGE_SIZE,
			toolbar: {
				save: { enabled: this.hasWriteAccess() },
				discard: { enabled: this.hasWriteAccess() },
				new: { enabled: this.hasWriteAccess() },
				rowPasteMode: { enabled: this.hasWriteAccess() },
			},
			showEmptyRow: true,
			rowMenu: {
				addRows: true,
				deleteRows: true,
			},
		});
	}

	hasWriteAccess(): boolean {
		return rp.userProvider.hasRight(AccountingRights.MDMDefine) || rp.userProvider.hasRight(AccountingRights.MDMWrite);
	}

	async save(rowData: RowData[]) {
		const dateColumns = this._columns.filter(col => col.t === 'd').map(col => col.data);
		const data = rowData.map(row => {
			const item: Record<string, any> = { _state: row.state, _index: row.index, id_md: this.table.id_md, ...row.data };
			// Replace undefined with null => we need all columns to be present
			for (const key of Object.keys(item)) item[key] ??= null;
			// convert date Excel serial numbers back to date strings
			for (const col of dateColumns) {
				if (item[col] && typeof item[col] === 'number')
					item[col] = formatDateTime(item[col] as number, 'yyyy-MM-dd', true);
			}
			return item;
		});
		const saved = await rp.ms('mdm').post<Record<string, unknown>[]>(`values`, data, { type: this.table.type });
		this.mergeInsertedRows(data, saved);
	}

	/**
	 * Merge the generated values for inserted rows returned by the server into the grid model.
	 */
	private mergeInsertedRows(sent: Record<string, any>[], saved: Record<string, unknown>[]): void {
		if (!Array.isArray(saved) || !saved.length) return;
		const grid = this.editor?.grid;
		if (!grid) return;

		const idzCol = grid.model.columns.find(c => c.data === 'IDZ')?.index;
		if (idzCol == null) return; // no primary key column → nothing to merge

		let savedIndex = 0;
		let insertedRows = false;
		for (const item of sent) {
			if (item._state === 'deleted') continue; // deleted rows are not returned by the server
			const values = saved[savedIndex++];
			if (!values || values.IDZ == null) continue;

			const cell = grid.model.getCellByCoords(item._index, idzCol);
			if (!cell) continue;
			cell.value = values.IDZ; // grid coerces per column type, same as the reload path
			grid.rendering.updateCells([cell]);
			insertedRows = true;
		}
	}
}
